<template>
    <Head :title="t('myDevelopment.title')" />
    <BaseLayout gap="none">
        <div ref="homeContent" class="mx-auto flex w-full max-w-208 flex-col gap-10">
            <output :aria-label="t('myDevelopment.title')" class="scaffold-body-small text-ink-muted" :class="{ 'sr-only': !!data || !isFetching }">
                <span v-if="isFetching && !data">{{ t("common.loading") }}</span>
                <span v-else-if="feedback">{{ feedback }}</span>
            </output>
            <template v-if="loadFailed">
                <BasePageHeader :title="t('myDevelopment.title')" tabindex="-1" data-home-heading />
                <BaseNotification theme="error" :text="t('myDevelopment.loadError')" hide-dismiss />
                <BaseButton theme="secondary" class="self-start" :disabled="isFetching" @click="execute()">{{ t("common.tryAgain") }}</BaseButton>
            </template>
            <template v-else-if="data">
                <BaseNotification v-if="mutationFailed && !isDeleteDialogOpen" theme="error" :text="t('myDevelopment.updateError')" hide-dismiss />
                <MyDevelopmentHome
                    :data="data"
                    :busy="isMutating || isFetching || isStartingChat"
                    @focus="mutateGoal($event, 'focus')"
                    @plan="mutateGoal($event, 'plan')"
                    @new-goal="startChat('new_goal')"
                    @ongoing="startChat('ongoing', $event?.id)"
                    @delete="confirmDeletion" />
            </template>
        </div>
    </BaseLayout>
    <ConfirmationDialog
        v-model:open="isDeleteDialogOpen"
        :title="t('myDevelopment.deleteGoalTitle')"
        :description="t('myDevelopment.deleteGoalDescription', { title: goalToDelete?.title ?? '' })"
        :text="t('myDevelopment.deleteGoalDescription', { title: goalToDelete?.title ?? '' })"
        :confirm-label="t('myDevelopment.deleteGoal')"
        :confirm-disabled="isMutating"
        :cancel-disabled="isMutating"
        :close-on-confirm="false"
        :return-focus-to="dialogReturnTarget"
        @confirm="goalToDelete && mutateGoal(goalToDelete, 'delete')">
        <template #content>
            <BaseNotification v-if="mutationFailed" theme="error" :text="t('myDevelopment.updateError')" hide-dismiss />
        </template>
    </ConfirmationDialog>
</template>

<script setup lang="ts">
import { Head, router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type {
    MyDevelopmentChatIntent,
    MyDevelopmentChatStartRequest,
    MyDevelopmentChatStartResponse,
    MyDevelopmentGoal,
    MyDevelopmentGoalFocusUpdate,
    MyDevelopmentHome as MyDevelopmentHomeData,
} from "~vue/api/generated/my-development";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import BasePageHeader from "~vue/components/BasePageHeader.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import MyDevelopmentHome from "~vue/pages/myDevelopment/MyDevelopmentHome.vue";
import { isInIFrame, openUrl } from "~vue/utils";
import { computed, nextTick, onBeforeUnmount, ref, shallowRef, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

defineOptions({ layout: LeftSidebarLayout });
const { t } = useI18n();
const { data, error, isFetching, isFinished, statusCode, execute } = useFetch("/v1/my-development/home").get().json<MyDevelopmentHomeData>();
const loadFailed = computed(() => !!error.value || (isFinished.value && statusCode.value === null));
const isMutating = ref(false);
const isStartingChat = ref(false);
const mutationFailed = ref(false);
const feedback = ref("");
const goalToDelete = ref<MyDevelopmentGoal | null>(null);
const isDeleteDialogOpen = ref(false);
const homeContent = useTemplateRef("homeContent");
const dialogReturnTarget = shallowRef<HTMLElement | null>(null);
let navigationVersion = 0;
const stopTrackingNavigation = router.on("start", () => navigationVersion++);
onBeforeUnmount(() => {
    navigationVersion++;
    stopTrackingNavigation();
});

function goalFocusTarget(goalId?: string): HTMLElement | null {
    const rows = homeContent.value?.querySelectorAll<HTMLElement>("[data-goal-id]") ?? [];
    const row = Array.from(rows).find((element) => element.dataset.goalId === goalId);
    return row?.querySelector<HTMLElement>('button[aria-haspopup="menu"]') ?? homeContent.value?.querySelector<HTMLElement>("[data-home-heading]") ?? null;
}

function confirmDeletion(goal: MyDevelopmentGoal) {
    goalToDelete.value = goal;
    dialogReturnTarget.value = goalFocusTarget(goal.id);
    mutationFailed.value = false;
    isDeleteDialogOpen.value = true;
}

type GoalMutationAction = "focus" | "delete" | "plan";

function goalMutationFeedback(goal: MyDevelopmentGoal, action: GoalMutationAction): string {
    if (action === "delete") {
        return t("myDevelopment.goalDeleted");
    }
    return goal.is_focused ? t("myDevelopment.focusRemoved") : t("myDevelopment.focusAdded");
}

async function restoreFocusAfterMutation(goalId: string | undefined, action: GoalMutationAction, saved: boolean, expectedNavigationVersion: number) {
    await nextTick();
    if (isStartingChat.value || expectedNavigationVersion !== navigationVersion) {
        return;
    }
    const target = goalFocusTarget(goalId);
    if (action === "delete") {
        if (saved) {
            dialogReturnTarget.value = target;
            isDeleteDialogOpen.value = false;
        }
    } else {
        target?.focus();
    }
}

async function startChat(intent: MyDevelopmentChatIntent, goalId?: string) {
    if (isStartingChat.value) {
        return;
    }
    isStartingChat.value = true;
    mutationFailed.value = false;
    const expectedNavigationVersion = navigationVersion;
    try {
        const payload: MyDevelopmentChatStartRequest = { intent, goal_id: goalId };
        const request = useFetch("/v1/my-development/chat/start", { immediate: false }).post(payload).json<MyDevelopmentChatStartResponse>();
        const response = await request.execute();
        if (expectedNavigationVersion !== navigationVersion) {
            isStartingChat.value = false;
            return;
        }
        const destination = request.data.value?.redirect_url;
        if (request.error.value || !response?.ok || !destination) {
            mutationFailed.value = true;
            isStartingChat.value = false;
        } else if (isInIFrame()) {
            openUrl(destination);
        } else {
            router.visit(destination, { onFinish: () => (isStartingChat.value = false) });
        }
    } catch {
        isStartingChat.value = false;
        mutationFailed.value = true;
    }
}

async function mutateGoal(goal: MyDevelopmentGoal, action: GoalMutationAction) {
    if (isMutating.value || isStartingChat.value) {
        return;
    }
    isMutating.value = true;
    mutationFailed.value = false;
    feedback.value = "";
    const startedNavigationVersion = navigationVersion;
    const goals = data.value?.goals ?? [];
    const displayedGoals = [...goals.filter((item) => item.is_focused), ...goals.filter((item) => !item.is_focused)];
    const index = displayedGoals.findIndex((item) => item.id === goal.id);
    const returnGoalId = action === "delete" ? (displayedGoals[index + 1] ?? displayedGoals[index - 1])?.id : goal.id;
    let saved = false;
    try {
        const request = useFetch(`/v1/my-development/goals/${goal.id}${action !== "delete" ? "/focus" : ""}`, { immediate: false });
        if (action !== "delete") {
            const payload: MyDevelopmentGoalFocusUpdate = { is_focused: action === "plan" || !goal.is_focused };
            request.patch(payload);
        } else {
            request.delete();
        }
        const response = await request.execute();
        if (request.error.value || !response?.ok) {
            mutationFailed.value = true;
            return;
        }
        saved = true;
        const refreshed = await execute();
        if (!refreshed?.ok || error.value) {
            return;
        }
        feedback.value = goalMutationFeedback(goal, action);
        if (action === "plan") {
            if (startedNavigationVersion !== navigationVersion) {
                return;
            }
            await startChat("build_plan", goal.id);
        }
    } catch {
        isStartingChat.value = false;
        mutationFailed.value = true;
    } finally {
        isMutating.value = false;
        await restoreFocusAfterMutation(saved ? returnGoalId : goal.id, action, saved, startedNavigationVersion);
    }
}
</script>
